import { TrackedChange } from 'models/changes'
import React, { lazy, memo, Suspense, useMemo } from 'react'
import { CardContentProps } from './CardContent'

const LazyActionButtons = lazy(() =>
    import('./ActionButtons').then((module) => ({
        default: module.ActionButtons,
    }))
)
const LazyUserAvatarAndDetails = lazy(() =>
    import('./UserAvatarAndDetails').then((module) => ({
        default: module.UserAvatarAndDetails,
    }))
)
interface TrackChangeCardHeaderProps extends CardContentProps {
    trackChanges?: TrackedChange
}

/**
 * Header subComponent for the track change card.
 * Renders user avatar, details, and action buttons if applicable.
 * @param {TrackChangeCardHeaderProps} props - The component props.
 * @returns {JSX.Element} The rendered header for the track change card.
 */
export const TrackChangeCardHeader = memo(
    ({
        trackChanges,
        cardSuggestion,
        classes,
        cardType,
    }: TrackChangeCardHeaderProps) => {
        const shouldShowActionButtons = useMemo(
            () =>
                trackChanges &&
                !cardType.isApprovedAdded &&
                !cardType.isRejectedAdded &&
                !cardType.isApprovedDeleted &&
                !cardType.isRejectedDeleted,
            [
                trackChanges,
                cardType.isApprovedAdded,
                cardType.isRejectedAdded,
                cardType.isApprovedDeleted,
                cardType.isRejectedDeleted,
            ]
        )
        const userImageUrl = useMemo(
            () => trackChanges?.userProfileInfo?.imageUrl,
            [trackChanges?.userProfileInfo?.imageUrl]
        )

        return (
            <Suspense fallback={<div className={classes?.placeholderHeader} />}>
                <LazyUserAvatarAndDetails
                    imageUrl={userImageUrl}
                    trackChanges={trackChanges}
                    cardSuggestion={cardSuggestion}
                    classes={classes}
                >
                    {shouldShowActionButtons && (
                        <LazyActionButtons
                            cardSuggestion={cardSuggestion}
                            trackChanges={trackChanges}
                            classes={classes}
                        />
                    )}
                </LazyUserAvatarAndDetails>
            </Suspense>
        )
    }
)

TrackChangeCardHeader.displayName = 'TrackChangeCardHeader'
